import { Fragment, type ReactNode } from 'react'
import { formatRevenueCompact } from './formatters'
import YtdLine from './YtdLine'
import {
  flattenPlanNodes,
  type PlanNodeFromServer,
} from './VersionViewOverallSection'
import { BigMoveArrow, DiffLine, DiffValue, RowSign, addedRowStyle, isChanged, publishedValue, type RevisionBaseline } from './DiffBits'
import RowCommentButton from './RowCommentButton'
import { type RowAnchor } from './rowAnchor'
import { type CellAnchor } from './cellAnchor'
import CommentableCell, { CellMark, type CellMarkState } from './CommentableCell'

interface Channel { id: string; name: string; slug: string; position?: number }
interface MatrixRow {
  plan_node_id: string
  past: Record<string, string>
  plan: Record<string, number>
  ytd?: Record<string, string>
  // Actual sales for the plan's actual months. Present only on a revision.
  actual?: Record<string, string>
}

// Month labels for a revision's actual-vs-plan split; null for a plain version.
type PeriodLabels = { actualQuarters: string; quarters: string } | null

// With `baseline` (a revision under review), GitHub diff styling: a cell
// that differs from the published plan shows the new value on a green line
// over the published value on a red line. A published cell that no longer
// exists counts as 0 on either side; rows the revision added are green with
// a + sign instead of per-cell marks.
//
// With `periodLabels` (a quarterly revision), every cell stacks its lines —
// sold months, remaining plan, this version's full year and, under review,
// the published version's full year — named once per row by a label column
// after the node name (the same idea as the editor's row labels). The two
// full-year lines are coloured only where they differ. The Total column gains
// a "Full year" line (actual + plan).
export default function VersionViewChannelSection({
  channels,
  rows,
  planNodes,
  currency,
  periodLabels = null,
  baseline = null,
  onRowAction,
  rowExtras,
  onCellAction,
  cellCommentCount,
  cellSelected,
}: {
  channels: Channel[]
  rows: MatrixRow[]
  planNodes: PlanNodeFromServer[]
  currency?: string | null
  periodLabels?: PeriodLabels
  baseline?: RevisionBaseline | null
  onRowAction?: (anchor: RowAnchor) => void
  rowExtras?: (anchor: RowAnchor) => ReactNode
  onCellAction?: (cell: CellAnchor) => void
  cellCommentCount?: (cell: CellAnchor) => number
  cellSelected?: (cell: CellAnchor) => boolean
}) {
  const flat = flattenPlanNodes(planNodes)
  const colCount = 2 + channels.length + (periodLabels ? 1 : 0)
  // Line heights shared by the label column and the cells, so the words sit
  // beside the numbers they name.
  const LINE = { actual: 16, plan: 20, diff: 20 } as const
  // Published value for a cell on a matched (not added) row; undefined = no diff.
  const wasFor = (nodeId: string, channelId: string): number | undefined => {
    if (!baseline || !(nodeId in baseline.node_targets)) return undefined
    return baseline.channel_targets[nodeId]?.[channelId] ?? 0
  }
  const planByNode = new Map<string, Record<string, number>>()
  const ytdByNode = new Map<string, Record<string, string>>()
  const actualByNode = new Map<string, Record<string, string>>()
  rows.forEach((r) => {
    planByNode.set(r.plan_node_id, r.plan)
    ytdByNode.set(r.plan_node_id, r.ytd ?? {})
    actualByNode.set(r.plan_node_id, r.actual ?? {})
  })
  return (
    <div className="border rounded-md overflow-x-auto" style={{ borderColor: 'var(--border)', background: 'var(--background)' }}>
      <table className="w-full text-sm border-collapse">
        <thead className="bg-muted/40">
          <tr className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <th className="text-left px-3 py-2 border-b sticky left-0 bg-muted/40" style={{ width: 200, borderColor: 'var(--border)' }}>Plan node</th>
            {periodLabels && <th className="border-b" style={{ width: 96, borderColor: 'var(--border)' }} aria-hidden />}
            {channels.map((c) => (
              <th key={c.id} className="text-right px-2 py-2 border-b uppercase tracking-wide" style={{ borderColor: 'var(--border)' }}>
                {c.name}
              </th>
            ))}
            <th className="text-right px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {flat.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="px-3 py-4 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                No plan nodes.
              </td>
            </tr>
          ) : (
            flat.map((n) => {
              const planRow = planByNode.get(n.id) ?? {}
              const ytdRow = ytdByNode.get(n.id) ?? {}
              const actualRow = actualByNode.get(n.id) ?? {}
              const rowSum = channels.reduce((s, c) => s + (planRow[c.id] ?? 0), 0)
              const rowYtdSum = channels.reduce((s, c) => s + Number(ytdRow[c.id] ?? 0), 0)
              const rowActualSum = channels.reduce((s, c) => s + Number(actualRow[c.id] ?? 0), 0)
              const added = baseline?.added_node_ids.includes(n.id) ?? false
              const anchor: RowAnchor = { section: 'channel', anchor_type: 'TopDownPlanNode', anchor_id: n.id, label: n.name }
              const extra = rowExtras?.(anchor)
              return (
                <Fragment key={n.id}>
                  <tr className={onRowAction ? 'group' : undefined} style={added ? addedRowStyle : undefined}>
                    <td
                      className="relative px-3 py-2 border-b sticky left-0 bg-background whitespace-nowrap"
                      style={{ borderColor: 'var(--border)', paddingLeft: (onRowAction ? 32 : 12) + n.depth * 16, color: 'var(--foreground)', fontWeight: n.hasChildren ? 500 : 400, ...(added ? addedRowStyle : {}) }}
                    >
                      {onRowAction && <RowCommentButton label={n.name} onClick={() => onRowAction(anchor)} />}
                      {added && <RowSign kind="added" />}
                      {n.name}
                    </td>
                    {periodLabels && <RowLabels labels={periodLabels} versions={baseline?.version_numbers} line={LINE} />}
                    {channels.map((c) => {
                      const cell: CellAnchor = { ...anchor, column_key: c.id, column_label: c.name }
                      const v = planRow[c.id] ?? 0
                      const ytd = Number(ytdRow[c.id] ?? 0)
                      const actual = Number(actualRow[c.id] ?? 0)
                      const was = wasFor(n.id, c.id)
                      // On a revision the diff reads on the full year (sold + plan).
                      const fullYearNow = baseline?.channel_full_year?.[n.id]?.[c.id] ?? (actual + v)
                      const mark = { count: cellCommentCount?.(cell) ?? 0 }
                      return (
                        <CommentableCell
                          key={c.id}
                          cell={cell}
                          onCellAction={onCellAction}
                          selected={cellSelected?.(cell) ?? false}
                          className="px-2 py-2 border-b text-right tabular-nums"
                          style={{ borderColor: 'var(--border)', color: v ? 'var(--foreground)' : 'var(--muted-foreground)' }}
                        >
                          {periodLabels
                            ? <RevisionLines actual={actual} plan={v} was={was} now={fullYearNow} withDiff={!!baseline} currency={currency} line={LINE} mark={mark} />
                            : (
                              <>
                                <DiffValue was={was} now={v} format={(x) => (x ? formatRevenueCompact(x, currency) : '—')} mark={mark} />
                                <YtdLine ytd={ytd} target={v} currency={currency} />
                              </>
                            )}
                        </CommentableCell>
                      )
                    })}
                    <td className="px-3 py-2 border-b text-right tabular-nums" style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                      {formatRevenueCompact(rowSum, currency)}
                      <YtdLine ytd={rowYtdSum} target={rowSum} currency={currency} />
                      {periodLabels && (
                        <div className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>
                          Full year {formatRevenueCompact(rowActualSum + rowSum, currency)}
                        </div>
                      )}
                    </td>
                  </tr>
                  {extra && (
                    <tr>
                      <td colSpan={colCount} className="p-0 border-b" style={{ borderColor: 'var(--border)' }}>{extra}</td>
                    </tr>
                  )}
                </Fragment>
              )
            })
          )}
          {flat.length > 0 && (() => {
            const channelTotals = channels.map((c) =>
              planNodes.reduce((s, n) => s + (planByNode.get(n.id)?.[c.id] ?? 0), 0),
            )
            const grandTotal = channelTotals.reduce((s, v) => s + v, 0)
            const channelYtdTotals = channels.map((c) =>
              planNodes.reduce((s, n) => s + Number(ytdByNode.get(n.id)?.[c.id] ?? 0), 0),
            )
            const grandYtdTotal = channelYtdTotals.reduce((s, v) => s + v, 0)
            const channelActualTotals = channels.map((c) =>
              planNodes.reduce((s, n) => s + Number(actualByNode.get(n.id)?.[c.id] ?? 0), 0),
            )
            const grandActualTotal = channelActualTotals.reduce((s, v) => s + v, 0)
            return (
              <tr style={{ borderTop: '2px solid var(--border)' }}>
                <td
                  className="px-3 py-2 sticky left-0 bg-background font-semibold whitespace-nowrap"
                  style={{ color: 'var(--foreground)' }}
                >
                  Summary
                </td>
                {periodLabels && <RowLabels labels={periodLabels} versions={baseline?.version_numbers} line={LINE} />}
                {channelTotals.map((v, i) => {
                  const was = publishedValue(baseline, (b) => b.channel_totals[channels[i].id])
                  const fullYearNow = baseline?.channel_totals_full_year?.[channels[i].id] ?? (channelActualTotals[i] + v)
                  return (
                    <td
                      key={channels[i].id}
                      className="px-2 py-2 text-right tabular-nums font-semibold"
                      style={{ color: v ? 'var(--foreground)' : 'var(--muted-foreground)' }}
                    >
                      {periodLabels
                        ? <RevisionLines actual={channelActualTotals[i]} plan={v} was={was} now={fullYearNow} withDiff={!!baseline} currency={currency} line={LINE} />
                        : (
                          <>
                            <DiffValue was={was} now={v} format={(x) => (x ? formatRevenueCompact(x, currency) : '—')} />
                            <YtdLine ytd={channelYtdTotals[i]} target={v} currency={currency} />
                          </>
                        )}
                    </td>
                  )
                })}
                <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>
                  {formatRevenueCompact(grandTotal, currency)}
                  <YtdLine ytd={grandYtdTotal} target={grandTotal} currency={currency} />
                  {periodLabels && (
                    <div className="text-[11px] font-normal" style={{ color: 'var(--muted-foreground)' }}>
                      Full year {formatRevenueCompact(grandActualTotal + grandTotal, currency)}
                    </div>
                  )}
                </td>
              </tr>
            )
          })()}
        </tbody>
      </table>
    </div>
  )
}

type LineHeights = { actual: number; plan: number; diff: number }

// The one label column of a revision row: names the lines every cell stacks,
// at the same heights the cells use. Under review the two full-year lines
// carry their version numbers ("Full year v3" over "Full year v2").
function RowLabels({ labels, versions, line }: {
  labels: NonNullable<PeriodLabels>
  versions?: { now: number; was: number }
  line: LineHeights
}) {
  const cls = 'flex items-center whitespace-nowrap'
  return (
    <td className="px-2 py-2 border-b text-[10px] uppercase tracking-wide" style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}>
      <div className={cls} style={{ height: line.actual }}>{labels.actualQuarters} Actual</div>
      <div className={cls} style={{ height: line.plan }}>{labels.quarters} Plan</div>
      <div className={cls} style={{ height: line.diff }}>Full year{versions ? ` v${versions.now}` : ''}</div>
      {versions && <div className={cls} style={{ height: line.diff }}>Full year v{versions.was}</div>}
    </td>
  )
}

// A revision cell: sold months, remaining plan, this version's full year and,
// under review (withDiff), the published version's full year — always both
// lines then, coloured only when they differ.
function RevisionLines({ actual, plan, was, now, withDiff, currency, line, mark }: {
  actual: number
  plan: number
  was: number | undefined
  now: number
  withDiff: boolean
  currency?: string | null
  line: LineHeights
  mark?: CellMarkState
}) {
  const compact = (x: number) => (x ? formatRevenueCompact(x, currency) : '—')
  const changed = isChanged(was, now)
  const muted = { color: 'var(--muted-foreground)' }
  return (
    <>
      <div className="flex items-center justify-end text-[11px]" style={{ height: line.actual, ...muted }}>{compact(actual)}</div>
      <div className="flex items-center justify-end" style={{ height: line.plan }}><CellMark mark={mark}>{compact(plan)}</CellMark></div>
      <div className="flex flex-col justify-center text-[13px]" style={{ height: line.diff }}>
        {changed ? <DiffLine kind="added"><span className="whitespace-nowrap"><BigMoveArrow was={was} now={now} />{compact(now)}</span></DiffLine> : <div className="text-right" style={muted}>{compact(now)}</div>}
      </div>
      {withDiff && (
        <div className="flex flex-col justify-center text-[13px]" style={{ height: line.diff }}>
          {changed
            ? <DiffLine kind="removed" className="text-[13px]">{was ? compact(was) : '—'}</DiffLine>
            : <div className="text-right" style={muted}>{was === undefined ? '—' : compact(was)}</div>}
        </div>
      )}
    </>
  )
}
